<template>
<v-layout row wrap v-if="!isDataLoading || !isStoreLoading || getAvailableMenus === null || filteredMenus.length === 0" class="sticky-parent mb-5">
    <PopupModal v-if="shouldShowModal && popUp" :imagePth="imagePath" :loading="imgLoading" />
    <PopupModal v-if="shouldShowReferralModal && popUp" :imagePth="imageReferralPath" :loading="imgReferralLoading" />
  <v-flex row xs12 display-1 py-3
    class="MenuSelectTitle"
    :class="{
      'headline': $vuetify.breakpoint.smAndDown,
      'display-1': $vuetify.breakpoint.mdAndUp,
      'py-3': $vuetify.breakpoint.smAndDown,
      'py-1': $vuetify.breakpoint.mdAndUp
    }"
  >
    <div v-if="filteredMenus.length === 0 && !isDataLoading">
      {{$vuetify.t('$vuetify.menu.noMenu')}}
    </div>
    <div v-else-if="filteredMenus.length > 1">
      {{$vuetify.t('$vuetify.menu.selectMenu')}}
    </div>
  </v-flex>

  <v-layout row wrap grid-list-md class="MenuContainer justify-center">
    <v-flex v-for="menu in filteredMenus" :key="menu.code" xs12 sm4 md3 xl2 class="pa-3">
      <store-menu-view :menu="menu" />
    </v-flex>
  </v-layout>
</v-layout>
</template>

<script>
  import { mapActions, mapGetters } from "vuex";

  import StoreMenuView from "@/components/storeMenuView";
  import PopupModal from '@/views/PopupModal.vue'
import { isInSchedule } from "../services/schedule.service";

  export default {
    components: { StoreMenuView, PopupModal },
    data() {
      return {
        shouldShowModal: false,
        imagePath: null,
        shouldShowReferralModal: false,
        imageReferralPath: null,
        imgReferralLoading: true,
        imgLoading: true,
        isFirstVisit: false,
        popUp: false,
        isDataLoading: true,
        referrer: '',
        hasRedirected: false
      };
    },
    watch: {
      // Watch for changes in getOrderType and trigger a re-fetch if needed
      getOrderType: {
        handler: 'refetchMenusIfNeeded',
        immediate: true // Trigger the handler immediately on component mount
      },
      'getCheckoutInfo.scheduledDay': {
        handler(newVal, oldVal) {
          if (newVal !== oldVal) {
            this.refetchMenus();
          }
        },
        immediate: true
      },
      'getCheckoutInfo.scheduledTime': {
        handler(newVal, oldVal) {
          if (newVal !== oldVal) {
            this.refetchMenus();
          }
        },
        immediate: true
      }
    },

    computed: {
      ...mapGetters([
        'isStoreLoading',
        'getAvailableMenus',
        'isMenuChannelAllowed',
        'getStore',
        'getOrderType',
        'getLiteMenus',
        'getMenus',
        'getCheckoutInfo'
      ]),
      isFetchingMenus() {
        return this.getAvailableMenus === null || this.getLiteMenus === null;
      },
      filteredMenus() {
        try {
          if (this.getAvailableMenus && this.getAvailableMenus.length && this.getLiteMenus && this.getLiteMenus.length) {
            let shouldCheckSchedule = false;
            if (!this.getCheckoutInfo || (!this.getCheckoutInfo.scheduledDay && !this.getCheckoutInfo.scheduledTime)) {
              shouldCheckSchedule = false 
            } else {
              shouldCheckSchedule = true
            }
            const filteredMenus = this.getAvailableMenus.filter(menu => {
                if (menu.schedule && shouldCheckSchedule) {
                  const isAvailable = this.isMenuAvailable(menu);
                  if (!isAvailable) {
                    return false;
                  }
                }
              if(menu.channels && menu.channels.length) {
                return menu.channels.includes("ONLINE") && this.getLiteMenus.some(liteMenu => liteMenu.code === menu.code);
              } else {
                return this.getLiteMenus.some(liteMenu => liteMenu.code === menu.code)
              }
            });
            if (filteredMenus.length == 0){
              return this.getLiteMenus.map(liteMenu => {
                return {
                  ...liteMenu,
                  unavailable: true
                }
              })
            }
            return filteredMenus;
          } else {
            if (!this.getAvailableMenus) {
              this.handleApiLog('MenuSelect !getAvailableMenus. This is getMenus without filters', this.getMenus)
            } 
            if (this.getAvailableMenus.length == 0) {
              this.handleApiLog('MenuSelect getAvailableMenus is an empty array. This is getMenus without filters', this.getMenus)
            } 
            return [];
          }
        } catch (e) {
          this.handleApiError(e);
          return [];
        }
      }
    },
    methods: {
      isMenuAvailable(menu) {
        const scheduledDate = this.getCheckoutInfo.scheduledDay;
        const scheduledTime = this.getCheckoutInfo.scheduledTime;
        if (!menu.schedule || !menu.schedule.items || menu.schedule.items.length === 0) {
          return true; 
        }

        if (scheduledTime == 'now') {
          const storeTimeZone = this.getStore.timeZone || "America/Toronto";
          const now = new Date(
            new Date().toLocaleString("en-US", { timeZone: storeTimeZone })
          );
          return isInSchedule(menu.schedule.items, now, 0);
        }
        const targetDate = new Date(scheduledDate);
        const targetTime = new Date(scheduledTime);

        const targetDay = targetDate.toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
        const targetHour = targetTime.getHours() + targetTime.getMinutes() / 60;

        return menu.schedule.items.some(schedule => {
          if (schedule.type === 'DAYS_OF_WEEK' && schedule.days.includes(targetDay)) {
            return schedule.timeRange.some(range => {
              return targetHour >= range.start && targetHour <= range.end;
            });
          }
          return false;
        });
      },
      async refetchMenus() {
        try {
          this.isDataLoading = true;
          await this.fetchLiteMenus();
          if (this.filteredMenus.length === 1) {
            this.redirectToSingleMenu();
          }
        } catch (error) {
          console.error("Error while refetching menus:", error);
          this.handleApiError(error);
        } finally {
          this.isDataLoading = false;
        }
      },
      async initializeComponent() {
        this.isDataLoading = true;
        try {
            const { sid } = this.$route.params;
            await this.fetchStore({
                gid: this.getStore.groupId,
                sid
            });
            if (!this.getStore || !this.getStore.storeId) {
                throw new Error("Store data is missing or invalid.");
            }
            await this.fetchLiteMenus();
            if (this.filteredMenus.length === 1) {
                this.redirectToSingleMenu();
            } else {
                this.fetchShoppingCart();
                this.popUp = true;
            }
        } catch (error) {
            console.error("An error occurred during initialization:", error);
            this.handleApiError(error);
        } finally {
            this.isDataLoading = false;
        }
    },
      extractReferrer() {
        const urlParams = new URLSearchParams(window.location.search);
        this.referrer = urlParams.get('referrer') || '';
      },
      ...mapActions([ 'clearMenu', 'fetchLiteMenus', 'fetchStore', 'fetchShoppingCart' ]),
      async refetchMenusIfNeeded() {
        // Check the flag that you want to trigger a re-fetch
        if (this.getOrderType) {
          await this.fetchLiteMenus();
        }
      },
      async handleApiError(error) {
        try {
          this.$api.post('/order/error', {
            error: 'MenuSelect ERROR' + error,
            storeId: this.getStore.storeId
          });
        } catch (postError) {
          console.error('Error while handling API error:', postError);
        }
      },
      async handleApiLog(part, log) {
        let formattedMsg
        if (Array.isArray(log)) {
          const formattedLog = log.map(item => {
            if (typeof item === 'object') {
              return JSON.stringify(item);
            }
            return String(item);
          });
          formattedMsg = `MenuSelect ${part} ${formattedLog.join(', ')}`;
        }  else if (typeof log === 'object') {
          formattedMsg = `MenuSelect ${part} ${JSON.stringify(log)}`;
        } else {
          formattedMsg = `MenuSelect ${part} ${String(log)}`;
        }
        try {
          this.$api.post('/order/error', {
            error: formattedMsg,
            storeId: this.getStore.storeId
          });
        } catch (postError) {
          console.error('Error while handling API error:', postError);
        }
      },
      imagePth() {
        const store = this.getStore;
        let path;
        if (store &&
        store.settings &&
        store.settings.owaSettings &&
        store.settings.owaSettings.owaStoreNews && 
        store.settings.owaSettings.owaStoreNews.news && 
        store.settings.owaSettings.owaStoreNews.news.length
        ) {
          path = `${process.env.VUE_APP_API_IMAGE}${store.settings.owaSettings.owaStoreNews.news[0].imgUrl}`
          this.shouldShowModal = this.isFirstVisit;
        }
        this.imagePath = path
      },
      imageReferralPth() {
        const store = this.getStore;
        const referrerPath = this.$route.query.referrer;
        let path;
        if (store &&
        store.settings &&
        store.settings.rewardsSettings &&
        store.settings.rewardsSettings.referralSettings &&
        store.settings.rewardsSettings.referralSettings.referralBannerImageUrl &&
        store.settings.rewardsSettings.supportsReferralBonus &&
        referrerPath
        ) {
          path = `${process.env.VUE_APP_API_IMAGE}${store.settings.rewardsSettings.referralSettings.referralBannerImageUrl}`
          this.shouldShowReferralModal = this.isFirstVisit;
        }
        this.imageReferralPath = path
      },
      redirectToSingleMenu: _.debounce(function() {
        if (this.hasRedirected) return;
        this.hasRedirected = true;
        const referrerParam = this.referrer ? { referrer: this.referrer } : {};
        const referrerPath = this.$route.query.referrer;
        const { sid } = this.$route.params;
        const dineInOnlyQueryParam = this.$route.query.dineInOnly;
        const tid = this.$route.query.tid || this.$route.params.tid;
        const vc = this.$route.query.vc;
        const targetRoute = {
          name: 'store',
          params: {
              sid,
              mid: this.filteredMenus[0].code,
              restaurantSlug: this.getStore.slug,
              ...referrerParam
          },
          query: {
               ...this.$route.query,
              dineInOnly: dineInOnlyQueryParam,
              tid,
              skipBeforeEnter: true,
              vc
          }
        };
        if (
          this.$route.name === targetRoute.name &&
          JSON.stringify(this.$route.params) === JSON.stringify(targetRoute.params) &&
          JSON.stringify(this.$route.query) === JSON.stringify(targetRoute.query)
        ) {
            console.log('Navigation skipped: Already at the target route.');
            return;
        }

        if (this.filteredMenus && this.filteredMenus.length === 1) {
            localStorage.setItem('forcePopup', JSON.stringify(true));
            if (referrerParam || referrerPath) {
                localStorage.setItem('forcePopupReferral', JSON.stringify(true));
            }
            this.$router.replace(targetRoute);
        }
    }, 500)
    },
    beforeRouteEnter(to, from, next) {
      const isFirstVisit = from.name === null;
      next(vm => {
        vm.isFirstVisit = isFirstVisit;
        vm.imagePth();
      });
    },

 created() {
  this.initializeComponent();
}

}
</script>

<style scoped>
  .MenuSelectTitle {
    font-size: var(--text-size-3) !important;
    font-weight: 500;
  }
</style>